import React, { useState, useMemo } from "react";
import type { SaleData } from "../token/StatsBox";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import DonutWithLegend from "../token/DonutWithLegend";
import CustomTooltip from "../token/Tooltip";
import StatsBox, { BillingStats } from "../token/StatsBox";
import MonthlyRevenue from "../token/MonthlyRevenue";

interface DashboardAnalyticsProps {
  data: SaleData[];
}

const DashboardAnalytics: React.FC<DashboardAnalyticsProps> = ({ data }) => {
  const processedSales = data.map((p) => ({
    ...p,
    year: p.paymentDate?.getFullYear() ?? null,
    month: p.paymentDate?.getMonth() ?? null,
  }));

  const [barRange, setBarRange] = useState("Depuis toujours");
  const today = new Date();
  const currentYear = today.getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // ── Filtrer uniquement les projets payés ───────────────────────
  const paidSales = processedSales.filter(
    (p) => p.paymentDate && p.paymentDate.getTime() <= today.getTime()
  );

  // ── Donut distributions for selectedYear ────────────────────────
  const dataClientYear: { name: string; value: number }[] = Object.entries(
    paidSales
      .filter((p) => p.year === selectedYear)
      .reduce((map, p) => {
        const key = (p["Client"] as string) || p.clientId;
        map[key] = (map[key] || 0) + p.amount;
        return map;
      }, {} as Record<string, number>)
  )
    .map(([name, value]) => ({ name, value: value as number }))
    .sort((a, b) => b.value - a.value);

  const dataProvenanceYear: { name: string; value: number }[] = Object.entries(
    paidSales
      .filter((p) => p.year === selectedYear)
      .reduce((map, p) => {
        const key = (p["Provenance"] as string) || "Autres";
        map[key] = (map[key] || 0) + p.amount;
        return map;
      }, {} as Record<string, number>)
  )
    .map(([name, value]) => ({ name, value: value as number }))
    .sort((a, b) => b.value - a.value);

  const dataProduitYear: { name: string; value: number }[] = Object.entries(
    paidSales
      .filter((p) => p.year === selectedYear)
      .reduce((map, p) => {
        const key = (p["Produit"] as string) || "Autres";
        map[key] = (map[key] || 0) + p.amount;
        return map;
      }, {} as Record<string, number>)
  )
    .map(([name, value]) => ({ name, value: value as number }))
    .sort((a, b) => b.value - a.value);

  const dataSecteurYear: { name: string; value: number }[] = Object.entries(
    paidSales
      .filter((p) => p.year === selectedYear)
      .reduce((map, p) => {
        const key = (p["Secteur d’activité"] as string) || "Autres";
        map[key] = (map[key] || 0) + p.amount;
        return map;
      }, {} as Record<string, number>)
  )
    .map(([name, value]) => ({ name, value: value as number }))
    .sort((a, b) => b.value - a.value);

  // ── Labels & découpage en trimestres ───────────────────────────
  const monthLabels = [
    "Janv.",
    "Fév.",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juil.",
    "Août",
    "Sept.",
    "Oct.",
    "Nov.",
    "Déc.",
  ];
  const quarterRanges = [
    { name: "Q1", months: [0, 1, 2] },
    { name: "Q2", months: [3, 4, 5] },
    { name: "Q3", months: [6, 7, 8] },
    { name: "Q4", months: [9, 10, 11] },
  ];
  const currentQuarterIndex = Math.floor(today.getMonth() / 3);

  // ── Données mensuelles (chronologique) ─────────────────────────
  const dataMonthlyAll = monthLabels.map((label, idx) => ({
    name: label,
    value: paidSales
      .filter((p) => p.year === selectedYear && p.month === idx)
      .reduce((sum, p) => sum + p.amount, 0),
  }));

  // ── Données trimestrielles (par trimestre et année) ──────────────────────
  const dataQuarterlyAll = Object.entries(
    paidSales.reduce((acc, p) => {
      if (!p.startDate) return acc;
      const year = p.startDate.getFullYear();
      const month = p.startDate.getMonth();
      const quarterIndex = Math.floor(month / 3);
      const quarterName = quarterRanges[quarterIndex].name;
      const label = `${year} ${quarterName}`;
      acc[label] = (acc[label] || 0) + p.amount;
      return acc;
    }, {} as Record<string, number>)
  )
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => {
      const [yearA, qA] = a.name.split(" ");
      const [yearB, qB] = b.name.split(" ");
      const yearDiff = parseInt(yearA) - parseInt(yearB);
      if (yearDiff !== 0) return yearDiff;
      return (
        quarterRanges.findIndex((q) => q.name === qA) -
        quarterRanges.findIndex((q) => q.name === qB)
      );
    });

  const dataQuarterlyYear = quarterRanges.map(({ name, months }) => ({
    name: `${currentYear} ${name}`,
    value: paidSales
      .filter(
        (p) =>
          p.startDate?.getFullYear() === currentYear &&
          months.includes(p.startDate!.getMonth())
      )
      .reduce((sum, p) => sum + p.amount, 0),
  }));

  const dataQuarterlyTrim = dataQuarterlyAll.filter(
    (d) =>
      d.name === `${currentYear} ${quarterRanges[currentQuarterIndex].name}`
  );

  // ── Données annuelles (chronologique) ───────────────────────────
  const dataYearlyAll = Array.from(
    paidSales.reduce((map, p) => {
      const y = p.startDate?.getFullYear();
      if (y) map.set(y, (map.get(y) || 0) + p.amount);
      return map;
    }, new Map<number, number>())
  )
    .map(([y, value]) => ({ name: String(y), value }))
    .sort((a, b) => Number(a.name) - Number(b.name));

  const dataYearlyYear = dataYearlyAll.filter(
    (d) => Number(d.name) === currentYear
  );
  const dataYearlyTrim = dataYearlyYear;

  // ── Répartitions (DonutWithLegend) ─────────────────────────────
  const dataClientAll = Object.entries(
    paidSales.reduce((map, p) => {
      const key = (p["Client"] as string) || p.clientId;
      map[key] = (map[key] || 0) + p.amount;
      return map;
    }, {} as Record<string, number>)
  )
    .map(([name, value]) => ({ name, value: value as number }))
    .sort((a, b) => b.value - a.value);

  const dataProvenanceAll = Object.entries(
    paidSales.reduce((map, p) => {
      const key = (p["Provenance"] as string) || "Autres";
      map[key] = (map[key] || 0) + p.amount;
      return map;
    }, {} as Record<string, number>)
  )
    .map(([name, value]) => ({ name, value: value as number }))
    .sort((a, b) => b.value - a.value);

  const dataProduitAll = Object.entries(
    paidSales.reduce((map, p) => {
      const key = (p["Produit"] as string) || "Autres";
      map[key] = (map[key] || 0) + p.amount;
      return map;
    }, {} as Record<string, number>)
  )
    .map(([name, value]) => ({ name, value: value as number }))
    .sort((a, b) => b.value - a.value);

  const dataSecteurAll = Object.entries(
    paidSales.reduce((map, p) => {
      const key = (p["Secteur d’activité"] as string) || "Autres";
      map[key] = (map[key] || 0) + p.amount;
      return map;
    }, {} as Record<string, number>)
  )
    .map(([name, value]) => ({ name, value: value as number }))
    .sort((a, b) => b.value - a.value);

  const pickDataQuarterly = useMemo(() => {
    switch (barRange) {
      case "Cette année":
        return dataQuarterlyYear;
      case "Ce trimestre":
        return dataQuarterlyTrim;
      default:
        return dataQuarterlyAll;
    }
  }, [barRange, dataQuarterlyAll, dataQuarterlyYear, dataQuarterlyTrim]);

  const pickDataYearly = useMemo(() => {
    switch (barRange) {
      case "Cette année":
        return dataYearlyYear;
      case "Ce trimestre":
        return dataYearlyTrim;
      default:
        return dataYearlyAll;
    }
  }, [barRange, dataYearlyAll, dataYearlyYear, dataYearlyTrim]);

  return (
    <div className="w-full flex flex-col gap-6">
      {/* Header & Mensuel */}
      <div className="flex flex-col lg:flex-row gap-6">
        <MonthlyRevenue data={paidSales} />

        {/* Trimestriel & Annuel */}
        <div className="flex flex-col gap-6 flex-1">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-xl mb-2">
              Chiffre d’affaire trimestriel – {selectedYear}
            </h3>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={pickDataQuarterly} margin={{ bottom: 10 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#F1F1F1"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    tick={({ x, y, payload }) => {
                      const [year, quarter] = (payload.value as string).split(
                        " "
                      );
                      return (
                        <text x={x} y={y + 10} textAnchor="middle">
                          <tspan x={x} dy="0">
                            {quarter}
                          </tspan>
                          <tspan x={x} dy="1.2em">
                            {year}
                          </tspan>
                        </text>
                      );
                    }}
                  />
                  <YAxis
                    domain={[0, "dataMax"]}
                    tickFormatter={(value) =>
                      value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="value"
                    fill="#99C3FF"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-xl mb-2">
              Chiffre d’affaire annuel
            </h3>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={pickDataYearly}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#F1F1F1"
                    vertical={false}
                  />
                  <XAxis dataKey="name" interval={0} />
                  <YAxis
                    domain={[0, "dataMax"]}
                    tickFormatter={(value) =>
                      value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="value"
                    fill="#99C3FF"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Répartition du CA par client */}
      <DonutWithLegend
        title="Répartition du CA par client"
        dataAll={dataClientAll}
        dataYear={dataClientYear}
        dataTrim={dataClientAll}
      />

      <StatsBox data={paidSales} />

      {/* Provenance */}
      <DonutWithLegend
        title="Provenance des clients"
        dataAll={dataProvenanceAll}
        dataYear={dataProvenanceYear}
        dataTrim={dataProvenanceAll}
      />

      {/* Produit */}
      <DonutWithLegend
        title="Répartition du CA par produit"
        dataAll={dataProduitAll}
        dataYear={dataProduitYear}
        dataTrim={dataProduitAll}
      />

      {/* Devis & Factures */}
      <BillingStats data={processedSales} />

      {/* Secteur */}
      <DonutWithLegend
        title="Secteur d’activité"
        dataAll={dataSecteurAll}
        dataYear={dataSecteurYear}
        dataTrim={dataSecteurAll}
      />
    </div>
  );
};

export default DashboardAnalytics;
